import { arenaCard } from './arenaCards';
import './arenas.css';
import { characterRoom } from './characterRoom';
import { characterPreview } from './characterPreview';
import { defaultCharacterStyle, type CharacterStyle } from '../core/characters';
import { PLAYER_COLORS, PLAYER_COLORS_CB } from '../core/players';
import { builtInMatchLevels } from '../levels/catalog';
import { MAX_PLAYERS, PLAYER_COLOR_NAMES } from '../core/players';
import { DEFAULT_MAP, type PadMap } from '../input/remap';
import { cleanPeerName, isRoomCode, normalizeRoomCode } from '../net/relay';
import { WEAPON_DEFS, isOfficeWeapon, weaponDisplayName } from '../sim/weapons/defs';
import { DEFAULT_USER_SETTINGS, type UserSettings } from './settingsStore';
import type { BotDifficulty } from '../sim/rules/settings';
import { keyHelp, padHelp } from '../input/bindings';
import { buttonLabel, deviceLabel, type ControllerGlyphs, type InputDevice } from '../input/presentation';
import { choiceControl, type Choice } from './controls';
import './menus.css';
import './characterRoom.css';
import { MODE_CHOICES, MODE_HINTS, partyControls, partyOptions, partySummary, type PartyOptions } from './partyOptions';
import type { MatchRules, PartySnapshot } from '../net/protocol';
import { renderPartyHome, renderConnectedParty } from './partyLobby';

export type Screen = 'menu' | 'solo' | 'join' | 'character' | 'party' | 'settings' | 'pause' | 'scoreboard' | 'lobby' | 'editor' | 'play' | 'disconnect';

export type Seat = {
  taken: boolean;
  ready: boolean;
  color: number;
  padId: string | 'keyboard' | 'bot';
  name: string;
};

export type LobbyPeer = { id: number; name: string; slot: number; color: number; host: boolean; me: boolean; character?: CharacterStyle };

export type LobbyState = {
  phase: 'idle' | 'connecting' | 'connected';
  role: 'host' | 'client';
  name: string;
  peers: LobbyPeer[];
  /** Round trip to the relay in ms; 0 until measured. */
  ping: number;
  /** The host is mid-match (and this peer is watching from the lobby until the next one). */
  inMatch: boolean;
  shareUrl: string;
  /** Rules as the host has them, mirrored to clients so the lobby reads the same everywhere. */
  maxHp: number;
  firstTo: number;
  bots: number;
  party: PartySnapshot | null;
  partyRules: PartyOptions;
  rules?: MatchRules;
  presetId?: string;
};

export type MenuState = {
  screen: Screen;
  seats: Seat[];
  characterSeat: number;
  characterOrigin: 'menu' | 'join' | 'lobby';
  notice: string;
  firstTo: number;
  maxHp: number;
  bots: number;
  roomCode: string;
  botDifficulty: BotDifficulty;
  roundTimeLimit: number;
  chat: string[];
  draftChat: string;
  lobby: LobbyState;
  /** An online match is running behind the pause card (the sim does not stop for one player). */
  online: boolean;
  settingsOrigin: 'menu' | 'pause' | 'solo' | 'join' | 'lobby';
  settingsTab: SettingsTab;
  editorPlaytest: boolean;
  party: PartyOptions;
  partyOrigin: 'menu' | 'solo' | 'join' | 'lobby';
};

export type SettingsTab = 'feel' | 'controls' | 'match' | 'weapons' | 'arenas' | 'advanced';

export type MenuActions = {
  [key: string]: (() => void) | ((payload?: string) => void) | undefined;
};

export function createLobbyState(): LobbyState {
  return { phase: 'idle', role: 'client', name: '', peers: [], ping: 0, inMatch: false, shareUrl: '', maxHp: 100, firstTo: 0, bots: 0, party: null, partyRules: partyOptions() };
}

export function createMenuState(): MenuState {
  return {
    screen: 'menu',
    characterSeat: 0,
    characterOrigin: 'menu',
    seats: Array.from({ length: MAX_PLAYERS }, () => ({ taken: false, ready: false, color: 0, padId: '', name: '' })),
    notice: '',
    firstTo: 0,
    maxHp: 100,
    bots: 0,
    botDifficulty: 'standard',
    roundTimeLimit: 60,
    roomCode: '',
    chat: [],
    draftChat: '',
    lobby: createLobbyState(),
    online: false,
    settingsOrigin: 'menu',
    settingsTab: 'feel',
    editorPlaytest: false,
    party: partyOptions(),
    partyOrigin: 'solo',
  };
}

const COLORS = PLAYER_COLOR_NAMES;
export const MAX_SEATS = MAX_PLAYERS;

/** Step to the next palette entry nobody else at the table has claimed. */
export function cycleSeatColor(seat: Seat, dir: number, seats: Seat[] = []): void {
  const used = new Set(seats.filter((s) => s !== seat && s.taken).map((s) => s.color));
  let next = seat.color;
  for (let i = 0; i < COLORS.length; i++) {
    next = (next + dir + COLORS.length) % COLORS.length;
    if (!used.has(next)) break;
  }
  seat.color = next;
}

function freeColor(seats: Seat[]): number {
  const used = new Set(seats.filter((s) => s.taken).map((s) => s.color));
  for (let c = 0; c < COLORS.length; c++) if (!used.has(c)) return c;
  return 0;
}

/** A mouse joins the same single keyboard seat; repeated clicks never create a second fighter. */
export function takeMouseSeat(seats: Seat[], index: number): Seat | undefined {
  const existing = seats.find((seat) => seat.taken && seat.padId === 'keyboard');
  if (existing) return existing;
  const seat = seats[index];
  if (!seat || seat.taken) return undefined;
  seat.color = freeColor(seats);
  seat.taken = true;
  seat.ready = false;
  seat.padId = 'keyboard';
  seat.name = 'Mouse & keyboard';
  return seat;
}

/** First press takes the next free seat; the same pad/keyboard pressing again readies (PLAN 4.12). */
export function takeOrReadySeat(seats: Seat[], padId: string, name = ''): Seat | undefined {
  const existing = seats.find((s) => s.taken && s.padId === padId);
  if (existing) {
    existing.ready = true;
    return existing;
  }
  const empty = seats.find((s) => !s.taken);
  if (!empty) return undefined;
  empty.color = freeColor(seats);
  empty.taken = true;
  empty.padId = padId;
  empty.ready = false;
  empty.name = name || (padId === 'keyboard' ? 'Keyboard' : '');
  return empty;
}

/** Connection / first sighting of a pad: occupy a seat, do not ready yet. */
export function takeSeat(seats: Seat[], padId: string, name = ''): Seat | undefined {
  const existing = seats.find((s) => s.padId === padId);
  if (existing) {
    existing.taken = true;
    if (name) existing.name = name;
    return existing;
  }
  const empty = seats.find((s) => !s.taken);
  if (!empty) return undefined;
  empty.color = freeColor(seats);
  empty.taken = true;
  empty.padId = padId;
  empty.ready = false;
  empty.name = name;
  return empty;
}

export function clearSeats(seats: Seat[]): void {
  seats.forEach((s, i) => {
    s.taken = false;
    s.ready = false;
    s.color = i;
    s.padId = '';
    s.name = '';
  });
}

export function canStartMatch(seats: Seat[]): boolean {
  const players = seats.filter((seat) => seat.taken);
  return players.length > 0 && players.every((seat) => seat.ready);
}

/** How many bots fit beside the humans at the table. */
export function maxBots(seats: Seat[]): number {
  return MAX_SEATS - seats.filter((s) => s.taken).length;
}

/**
 * Palette index for every slot of a match: humans (taken seats, in order) keep what they picked,
 * bots take whatever is left, so no two fighters share a color.
 */
export function assignColors(seats: Seat[], total: number): number[] {
  const out: number[] = [];
  const used = new Set<number>();
  for (const s of seats.filter((s) => s.taken)) {
    let c = s.color;
    while (used.has(c)) c = (c + 1) % COLORS.length;
    used.add(c);
    out.push(c);
  }
  for (let c = 0; c < COLORS.length && out.length < total; c++) {
    if (!used.has(c)) out.push(c);
  }
  while (out.length < total) out.push(out.length % COLORS.length);
  return out.slice(0, total);
}

export function collectSettings(card: HTMLElement, menus: MenuState, settings: UserSettings): UserSettings {
  const num = (id: string, fallback: number) => {
    const el = card.querySelector(`#${id}`) as HTMLInputElement | null;
    return el ? Number(el.value) : fallback;
  };
  const chk = (id: string, fallback: boolean) => {
    const el = card.querySelector(`#${id}`) as HTMLInputElement | null;
    return el ? el.checked : fallback;
  };
  const sel = (id: string, fallback: string) => {
    const el = card.querySelector(`#${id}`) as HTMLSelectElement | null;
    return el ? el.value : fallback;
  };
  menus.maxHp = num('hp', menus.maxHp);
  menus.firstTo = num('ft', menus.firstTo);
  menus.bots = num('bots', menus.bots);
  const weapons = WEAPON_DEFS.filter((d) => d.dropWeight > 0 && !isOfficeWeapon(d.id));
  const enabledW = weapons.filter((d) => chk(`w-${d.id}`, true)).map((d) => d.id);
  const levels = builtInMatchLevels();
  const enabledL = levels.filter((l) => chk(`l-${l.id}`, true)).map((l) => l.id);
  let party = partyOptions(settings);
  try {
    const stored = card.querySelector<HTMLInputElement>('#settings-party-options');
    if (stored) party = partyOptions(JSON.parse(stored.value));
  } catch { /* Preserve the draft if a browser extension changes the hidden value. */ }
  let favoriteArenas = settings.favoriteArenas;
  try {
    const raw = card.querySelector<HTMLInputElement>('#arena-favorites')?.value;
    if (raw) favoriteArenas = JSON.parse(raw);
  } catch { /* Keep the existing draft. */ }
  return {
    ...settings,
    ...party,
    favoriteArenas,
    maxHp: menus.maxHp,
    firstTo: menus.firstTo,
    botDifficulty: sel('difficulty', settings.botDifficulty) as BotDifficulty,
    roundTimeLimit: num('round-limit', settings.roundTimeLimit),
    showWins: chk('wins', settings.showWins),
    haptics: chk('hap', settings.haptics),
    colorblind: chk('cb', settings.colorblind),
    reduceShake: chk('rs', settings.reduceShake),
    reduceBlood: chk('rb', settings.reduceBlood),
    showReplays: chk('replays', settings.showReplays),
    lighting: chk('lit', settings.lighting),
    includeUserLevels: chk('usr', settings.includeUserLevels),
    telemetry: chk('tel', settings.telemetry),
    controllerGlyphs: sel('controller-glyphs', settings.controllerGlyphs) as ControllerGlyphs,
    aimResponse: sel('aim-response', settings.aimResponse) as UserSettings['aimResponse'],
    aimRelease: sel('aim-release', settings.aimRelease) as UserSettings['aimRelease'],
    stickDeadzone: num('stick-deadzone', settings.stickDeadzone),
    renderer: sel('ren', settings.renderer) as UserSettings['renderer'],
    rotation: sel('rot', settings.rotation) as UserSettings['rotation'],
    sfx: num('sfx', settings.sfx),
    music: num('mus', settings.music),
    enabledWeapons: enabledW.length === weapons.length ? 'all' : enabledW,
    enabledLevels: enabledL.length === levels.length ? 'all' : enabledL,
  };
}

export function collectPadMap(card: HTMLElement): { padId: string; map: PadMap } {
  const padId = (card.querySelector('#padid') as HTMLInputElement | null)?.value || 'custom';
  const n = (id: string, fallback: number) => {
    const el = card.querySelector(`#${id}`) as HTMLInputElement | null;
    return el ? Number(el.value) : fallback;
  };
  return {
    padId,
    map: {
      jump: n('map-jump', DEFAULT_MAP.jump),
      attack: n('map-attack', DEFAULT_MAP.attack),
      block: n('map-block', DEFAULT_MAP.block),
      throw: n('map-throw', DEFAULT_MAP.throw),
      pause: n('map-pause', DEFAULT_MAP.pause),
    },
  };
}

export function renderMenus(
  root: HTMLElement,
  state: MenuState,
  actions: MenuActions,
  settings: UserSettings = DEFAULT_USER_SETTINGS,
  maps: Record<string, PadMap> = {},
  stats?: { matches: number; wins: number; kos: number },
  device: InputDevice = { kind: 'keyboard' },
): void {
  root.innerHTML = '';
  if (state.screen !== 'settings') settingsDrafts.delete(state);
  if (state.screen === 'play') return;
  const wrap = document.createElement('div');
  wrap.className = `menu-wrap menu-${state.screen}`;
  const card = document.createElement('div');
  card.className = 'menu-card';
  if (state.screen === 'menu') {
    renderPartyHome(card, state, settings, actions, stats);
  } else if (state.screen === 'solo') {
    card.classList.add('quick-match');
    card.innerHTML = `<h2>Quick Match</h2><p class="quick-mode-hint"></p>
      <div class="quick-options"></div><p class="dim quick-arena-hint"></p>
      <p class="dim quick-hint">New here? Practice walks you through movement, aim, blocking and throwing.</p>`;
    const hint = card.querySelector('.quick-mode-hint')!;
    const updateArenaHint = () => { card.querySelector('.quick-arena-hint')!.textContent = `${state.bots + 1} fighters · ${state.party.mode === 'office-brawl' ? 'Overtime office · Office equipment only' : state.bots >= 4 ? 'Large arenas with room for everyone' : 'Compact arenas for close fights'}`; };
    const updateMode = () => {
      state.party = partyOptions(state.party);
      hint.textContent = MODE_HINTS[state.party.mode];
      const length = card.querySelector<HTMLElement>('[data-choice="quick-length"]');
      if (length) length.hidden = state.party.mode === 'arms-race';
      updateArenaHint();
    };
    card.querySelector('.quick-options')!.append(
      choiceControl({ id: 'quick-mode', label: 'Game mode', choices: [...MODE_CHOICES], value: state.party.mode, onChange: (value) => { state.party.mode = value as PartyOptions['mode']; updateMode(); } }),
      choiceControl({ id: 'quick-difficulty', label: 'Bot difficulty', value: state.botDifficulty, choices: DIFFICULTIES, onChange: (value) => { state.botDifficulty = value as BotDifficulty; } }),
      choiceControl({ id: 'quick-length', label: 'Match length', value: state.firstTo, choices: MATCH_LENGTHS, onChange: (value) => { state.firstTo = Number(value); } }),
      choiceControl({ id: 'quick-bots', label: 'Opponents', value: state.bots, choices: botChoices(1, MAX_PLAYERS - 1), onChange: (value) => { state.bots = Number(value); updateArenaHint(); } }),
    );
    updateMode();
    updateArenaHint();
    const row = document.createElement('div');
    row.className = 'menu-actions';
    row.append(btn('Start', () => actions.quickStart?.(), 'primary'), btn('Party rules', () => actions.partyRules?.()), btn('Choose arenas', () => { state.settingsTab = 'arenas'; actions.settings?.(); }), btn('Practice', () => actions.practice?.()), btn('Back', () => actions.back?.()));
    card.append(row);
    const startHint = document.createElement('p');
    startHint.className = 'dim quick-start-hint';
    startHint.dataset.inputHint = 'quick-start';
    card.append(startHint);
  } else if (state.screen === 'character') {
    const color = state.characterOrigin === 'lobby'
      ? state.lobby.peers.find((peer) => peer.me)?.color ?? 0
      : state.seats[state.characterSeat]?.color ?? 0;
    characterRoom(card, color, settings, (style) => actions.characterChanged?.(JSON.stringify(style)), () => actions.characterBack?.());
  } else if (state.screen === 'join') {
    card.classList.add('local-play');
    const bots = Math.min(state.bots, maxBots(state.seats));
    card.innerHTML = `<h2>Local Play</h2><p data-input-hint="join"></p>`;
    const seats = document.createElement('div');
    seats.className = 'seats';
    // Humans sit in the seats they took, in order; bots fill the seats after them, so the card
    // shows exactly who will stand where when the countdown starts.
    const colors = assignColors(state.seats, MAX_SEATS);
    let botsLeft = bots;
    state.seats.forEach((s, i) => {
      const bot = !s.taken && botsLeft > 0;
      if (bot) botsLeft -= 1;
      const seat = document.createElement('div');
      seat.className = 'seat';
      seat.style.setProperty('--c', s.taken ? `var(--p${s.color})` : bot ? `var(--p${colors[i] ?? i})` : '#5a6170');
      seat.dataset.seat = String(i);
      seat.dataset.taken = s.taken ? '1' : '0';
      if (bot) seat.dataset.bot = '1';
      if (s.ready) seat.dataset.ready = '1';
      const colorIndex = s.taken ? s.color : colors[i] ?? i;
      const style = settings.characters[colorIndex] ?? defaultCharacterStyle(colorIndex);
      const face = characterPreview(style, (settings.colorblind ? PLAYER_COLORS_CB : PLAYER_COLORS)[colorIndex]!, { ready: s.ready, reducedMotion: settings.reduceShake });
      const name = document.createElement('div');
      name.className = 'seat-name';
      name.textContent = s.taken ? `P${i + 1} · ${COLORS[s.color]}` : bot ? `Bot ${i + 1}` : `P${i + 1}`;
      const status = document.createElement('div');
      status.className = 'seat-status';
      status.textContent = s.taken ? (s.ready ? 'READY' : 'joined — choose Ready') : bot ? 'CPU' : 'empty';
      const pad = document.createElement('div');
      pad.className = 'seat-pad';
      pad.textContent = s.taken ? s.name || s.padId : '';
      seat.append(face, name, status, pad);
      const controls = document.createElement('div');
      controls.className = 'seat-controls';
      if (s.taken) {
        const ready = btn(s.ready ? 'Not ready' : 'Ready', () => actions.readySeat?.(String(i)), s.ready ? '' : 'primary');
        ready.id = `seat-ready-${i}`;
        const previous = btn('‹', () => actions.colorSeat?.(`${i}:-1`));
        previous.classList.add('seat-color');
        previous.id = `seat-color-prev-${i}`;
        previous.setAttribute('aria-label', `Previous color for player ${i + 1}`);
        const next = btn('›', () => actions.colorSeat?.(`${i}:1`));
        next.classList.add('seat-color');
        next.id = `seat-color-next-${i}`;
        next.setAttribute('aria-label', `Next color for player ${i + 1}`);
        const label = document.createElement('span');
        label.className = 'seat-color-label';
        label.textContent = 'Color';
        const customize = btn('Customize', () => actions.characterSeat?.(String(i)));
        customize.id = `seat-character-${i}`;
        customize.setAttribute('aria-label', `Customize player ${i + 1}`);
        controls.append(ready, previous, label, next, customize);
      } else if (!state.seats.some((candidate) => candidate.taken && candidate.padId === 'keyboard') && i === state.seats.findIndex((candidate) => !candidate.taken)) {
        const join = btn('Join with mouse', () => actions.joinSeat?.(String(i)), 'primary');
        join.id = `seat-join-${i}`;
        controls.append(join);
      }
      seat.append(controls);
      seats.append(seat);
    });
    card.append(seats);
    const row = document.createElement('div');
    row.className = 'menu-actions';
    const start = btn('Start', () => actions.start?.(), 'primary');
    start.disabled = !canStartMatch(state.seats);
    row.append(start);
    const botsBtn = btn(`Bots: ${bots}`, () => actions.cycleBots?.());
    botsBtn.id = 'cycle-bots';
    row.append(botsBtn);
    row.append(btn('Party rules', () => actions.partyRules?.()));
    row.append(btn('Choose arenas', () => { state.settingsTab = 'arenas'; actions.settings?.(); }));
    row.append(btn('Back', () => actions.back?.()));
    card.append(row);
    const summary = document.createElement('p');
    summary.className = 'dim';
    summary.textContent = `${partySummary(state.party)} · ${state.seats.filter((s) => s.taken).length + bots} fighters${state.seats.filter((s) => s.taken).length + bots > 4 ? ' · Large arenas' : ''}`;
    card.append(summary);
  } else if (state.screen === 'party') {
    card.classList.add('party-card');
    card.innerHTML = '<h2>Party rules</h2><p>Choose a mode, mix in mutators, and make this match yours.</p>';
    const humans = state.partyOrigin === 'lobby' ? state.lobby.peers.length : state.seats.filter((seat) => seat.taken).length;
    const count = Math.min(MAX_PLAYERS, humans + state.bots);
    const slots = Array.from({ length: Math.max(2, count) }, (_, i) => i < humans ? state.partyOrigin === 'lobby' ? state.lobby.peers[i]?.name ?? `P${i + 1}` : `P${i + 1}` : `Bot ${i + 1}`);
    card.append(partyControls(state.party, 'party', (value) => { state.party = value; actions.partyChanged?.(); }, { advanced: true, slots }));
    const row = document.createElement('div');
    row.className = 'menu-actions party-actions';
    row.append(btn('Done', () => actions.back?.(), 'primary'), navigationHint());
    card.append(row);
  } else if (state.screen === 'settings') {
    renderSettings(card, state, settings, maps, actions);
  } else if (state.screen === 'pause') {
    card.classList.add('compact');
    if (state.online) {
      card.innerHTML = `<h2>Online match</h2><p>The fight goes on while this is open — nobody else's game pauses for you.</p>`;
    } else {
      card.innerHTML = `<h2>Paused</h2><p>Take a breather. The brawl waits.</p>`;
    }
    const row = document.createElement('div');
    row.className = 'menu-actions';
    row.append(btn('Resume', () => actions.resume?.(), 'primary'));
    row.append(btn('Settings', () => actions.settings?.()));
    if (state.editorPlaytest) row.append(btn('Back to editor', () => actions.edit?.()));
    row.append(btn(state.online ? (state.lobby.role === 'host' ? 'End match' : 'Leave match') : 'Quit', () => actions.quit?.()));
    card.append(row);
  } else if (state.screen === 'scoreboard') {
    card.innerHTML = `<h2 data-round-over="1">Round over</h2><p>Next level incoming…</p>`;
  } else if (state.screen === 'lobby') {
    renderLobby(card, state, settings, actions);
  } else if (state.screen === 'disconnect') {
    card.classList.add('compact');
    card.innerHTML = `<h2>Controller disconnected</h2><p>Reconnect the pad to pick up where you left off.</p>`;
    const row = document.createElement('div');
    row.className = 'menu-actions';
    row.append(btn('Resume anyway', () => actions.resume?.(), 'primary'));
    row.append(btn('Quit', () => actions.quit?.()));
    card.append(row);
  }
  if (state.notice) {
    const n = document.createElement('p');
    n.className = 'notice';
    n.textContent = state.notice;
    wrap.append(n);
  }
  if (['solo', 'pause'].includes(state.screen)) card.append(navigationHint());
  wrap.append(card);
  root.append(wrap);
  updateMenuInputHints(root, device, settings, maps);
}

/** Refresh only help text: changing devices must preserve focus, form drafts, and scroll. */
export function updateMenuInputHints(root: HTMLElement, device: InputDevice, settings: UserSettings, maps: Record<string, PadMap> = {}): void {
  const draft = root.querySelector<HTMLInputElement>('#controller-glyphs')?.value;
  const glyphs = (draft ?? settings.controllerGlyphs) as ControllerGlyphs;
  const pad = device.kind === 'gamepad';
  const label = (index: number) => buttonLabel(index, device, glyphs);
  const map = pad ? maps[device.id] : undefined;
  const help = (action: Parameters<typeof padHelp>[0]) => padHelp(action, device, glyphs, map);
  const text: Record<string, string> = {
    welcome: pad ? `Up to eight fighters. Press ${label(0)} to choose a mode.` : 'Up to eight fighters. Click a mode to play, or press a controller button.',
    device: deviceLabel(device, glyphs),
    gameplay: pad
      ? `Left stick move · Right stick aim · ${help('jump')} jump · ${help('attack')} attack · ${help('block')} block · ${help('throw')} throw · ${help('pause')} pause`
      : `A / D move · ${keyHelp('jump')} jump · S duck · Mouse aim · Left click attack · Right click block · F throw · Esc pause`,
    navigation: pad ? `D-pad / Left stick move · ${label(0)} choose · ${label(1)} back` : 'Click to choose · Arrow keys move · Enter choose · Esc back',
    join: pad ? `${label(0)} joins, again to ready. Left / right picks a color; up / down sets bots. ${label(2)} customize · ${label(8)} party rules · ${label(9)} begins.` : 'Click a seat to join, then Ready. Space also joins and readies; C customizes your fighter; arrows pick a color and set bots. Enter begins.',
    'quick-start': pad ? `${label(9)} starts the match from any option.` : 'Choose your options, then click Start.',
    'settings-controls': pad ? 'Button labels follow the controller you last used. Left / right changes a choice; up / down moves between rows.' : 'Click an option to change it. Button labels switch automatically when you use a controller.',
  };
  for (const node of root.querySelectorAll<HTMLElement>('[data-input-hint]')) {
    const value = text[node.dataset.inputHint ?? ''];
    if (value !== undefined && node.textContent !== value) node.textContent = value;
  }
}

const DIFFICULTIES: Choice[] = [{ value: 'beginner', label: 'Beginner' }, { value: 'standard', label: 'Standard' }, { value: 'expert', label: 'Expert' }];
const MATCH_LENGTHS: Choice[] = [{ value: 5, label: 'First to 5' }, { value: 10, label: 'First to 10' }, { value: 0, label: 'Endless' }];
const botChoices = (min: number, max: number): Choice[] => Array.from({ length: max - min + 1 }, (_, i) => ({ value: min + i, label: `${min + i} bot${min + i === 1 ? '' : 's'}` }));

function navigationHint(): HTMLParagraphElement {
  const hint = document.createElement('p');
  hint.className = 'nav-hint';
  hint.dataset.inputHint = 'navigation';
  return hint;
}

type SettingsDraft = { settings: UserSettings; maxHp: number; firstTo: number; bots: number; padId: string; map: PadMap };
const settingsDrafts = new WeakMap<MenuState, SettingsDraft>();

function renderSettings(
  card: HTMLElement,
  state: MenuState,
  settings: UserSettings,
  maps: Record<string, PadMap>,
  actions: MenuActions,
): void {
  let draft = settingsDrafts.get(state);
  if (!draft) {
    const localParty = ['menu', 'solo', 'join'].includes(state.settingsOrigin);
    const hostingParty = state.settingsOrigin === 'lobby' && state.lobby.role === 'host';
    draft = {
      settings: { ...settings, ...(localParty ? { ...partyOptions(state.party), botDifficulty: state.botDifficulty, roundTimeLimit: state.roundTimeLimit } : hostingParty ? { ...state.lobby.rules, ...partyOptions(state.party) } : {}) },
      maxHp: hostingParty ? state.lobby.maxHp : state.maxHp, firstTo: hostingParty ? state.lobby.firstTo : state.firstTo, bots: state.bots,
      padId: Object.keys(maps)[0] ?? '', map: { ...(Object.entries(maps)[0]?.[1] ?? DEFAULT_MAP) },
    };
    settingsDrafts.set(state, draft);
  }
  const current = draft.settings;
  const weaponBoxes = WEAPON_DEFS.filter((d) => d.dropWeight > 0 && !isOfficeWeapon(d.id))
    .map((d) => {
      const on = current.enabledWeapons === 'all' || current.enabledWeapons.includes(d.id);
      return `<label class="chip"><input id="w-${d.id}" type="checkbox" ${on ? 'checked' : ''}/> ${weaponDisplayName(d)}</label>`;
    })
    .join('');
  const check = (id: string, label: string, on: boolean) => `<label class="row"><span>${label}</span><input id="${id}" type="checkbox" ${on ? 'checked' : ''}></label>`;
  const slider = (id: string, label: string, value: number) => `<label class="row"><span class="sound-label">${label}<output for="${id}">${Math.round(value * 100)}%</output></span><input id="${id}" type="range" min="0" max="1" step="0.05" value="${value}"></label>`;
  const nextMatch = state.settingsOrigin === 'pause' ? 'Applies to the next match. Your current fight keeps its rules.' : 'Choose how your next match plays.';
  card.classList.add('settings-card');
  card.innerHTML = `<h2>Settings</h2>
    <div class="settings-tabs" role="tablist" aria-label="Settings categories"></div>
    <div class="settings-panels">
      <section class="settings-panel" id="settings-feel" role="tabpanel" aria-labelledby="settings-tab-feel" data-panel="feel">
        <div class="form-grid">
          ${slider('sfx', 'Sound effects', current.sfx)}
          ${slider('mus', 'Music', current.music)}
          ${check('hap', 'Controller vibration', current.haptics)}
          ${check('cb', 'Colorblind palette', current.colorblind)}
          ${check('rs', 'Reduce motion &amp; flashes', current.reduceShake)}
          ${check('rb', 'Reduce blood', current.reduceBlood)}
          ${check('replays', 'Final knockout replays', current.showReplays)}
        </div>
      </section>
      <section class="settings-panel" id="settings-controls" role="tabpanel" aria-labelledby="settings-tab-controls" data-panel="controls" hidden>
        <div data-control="aim-response"></div>
        <p class="dim">Steady softens small aiming corrections. Direct follows the stick immediately. Fast turns stay immediate with either choice.</p>
        <div data-control="aim-release"></div>
        <p class="dim">Hold aim keeps your last direction when you release the right stick. Follow movement turns your aim with your movement after a brief hold.</p>
        <div data-control="stick-deadzone"></div>
        <p class="dim">Raise this if a resting stick moves your fighter. Low makes gentle movements easier on controllers without drift.</p>
        <div class="controls-reference">
          <strong data-input-hint="device"></strong>
          <p class="dim" data-input-hint="gameplay"></p>
          <p class="dim" data-input-hint="settings-controls"></p>
        </div>
        <div class="controller-glyphs" data-control="controller-glyphs"></div>
        <p class="dim">Auto detects your controller. Choose labels here if your browser reports a different name.</p>
      </section>
      <section class="settings-panel" id="settings-match" role="tabpanel" aria-labelledby="settings-tab-match" data-panel="match" hidden>
        <p class="dim">${nextMatch}</p>
        <div data-control="hp"></div>
        <div data-control="ft"></div>
        <div data-control="bots"></div>
        <div data-control="difficulty"></div>
        <div data-control="round-limit"></div>
        <p class="dim" data-race-rules hidden>One arena · 2 kills per gun · Final blade wins. No round limit or sudden death.</p>
        <div data-party-settings></div>
        <div class="form-grid">${check('wins', 'Show round wins', current.showWins)}</div>
      </section>
      <section class="settings-panel" id="settings-weapons" role="tabpanel" aria-labelledby="settings-tab-weapons" data-panel="weapons" hidden>
        <p>Choose which weapons can drop.</p><p class="dim">${nextMatch} Arms Race uses its fixed ladder; Office Brawl uses its five office items.</p>
        <div class="collection-actions" data-collection-actions="weapons"></div>
        <div class="chips" data-collection="weapons">${weaponBoxes}</div>
      </section>
      <section class="settings-panel" id="settings-arenas" role="tabpanel" aria-labelledby="settings-tab-arenas" data-panel="arenas" hidden>
        <p class="dim">${nextMatch}</p>
        <p class="dim">Five to eight fighters use larger arenas. If your selection has no arena with enough player starts, a suitable large arena is chosen automatically. Office Brawl always uses the Overtime office.</p>
        <div data-control="rot"></div>
        <div class="form-grid">${check('usr', 'Include my saved arenas', current.includeUserLevels)}</div>
        <div class="collection-actions" data-collection-actions="arenas"></div>
        <input type="hidden" id="arena-favorites"/>
        <div class="arena-grid" data-collection="arenas"></div><p class="dim arena-empty">Star an arena to find it here.</p>
      </section>
      <section class="settings-panel" id="settings-advanced" role="tabpanel" aria-labelledby="settings-tab-advanced" data-panel="advanced" hidden>
        <div data-control="ren"></div>
        <div class="form-grid">
          ${check('lit', 'Emitter glow (enhanced graphics)', current.lighting)}
          ${check('tel', 'Share anonymous performance stats', current.telemetry)}
        </div>
        <p class="dim">Auto picks the best available graphics. Enhanced uses WebGPU; Classic works on older devices.</p>
        <p class="dim">Anonymous stats help improve performance. No account or cookies.</p>
        <details>
          <summary>Custom controller mapping</summary>
          <p class="dim">Standard controllers work automatically. For an unusual controller, enter its device name and button numbers.</p>
          <div class="form-grid">
            <label class="row wide"><span>Controller name</span><input id="padid" value="${esc(draft.padId)}" placeholder="Controller device name"></label>
            <label class="row"><span>Jump button</span><input id="map-jump" type="number" min="0" max="31" value="${draft.map.jump}"></label>
            <label class="row"><span>Attack button</span><input id="map-attack" type="number" min="0" max="31" value="${draft.map.attack}"></label>
            <label class="row"><span>Block button</span><input id="map-block" type="number" min="0" max="31" value="${draft.map.block}"></label>
            <label class="row"><span>Throw button</span><input id="map-throw" type="number" min="0" max="31" value="${draft.map.throw}"></label>
            <label class="row"><span>Pause button</span><input id="map-pause" type="number" min="0" max="31" value="${draft.map.pause}"></label>
          </div>
          <div class="menu-actions" data-remap-actions></div>
        </details>
      </section>
    </div>`;
  const customChoices = (choices: Choice[], value: number, label: string): Choice[] => choices.some((choice) => Number(choice.value) === value) ? choices : [...choices, { value, label }];
  const choices: { id: string; label: string; value: number | string; choices: Choice[] }[] = [
    { id: 'aim-response', label: 'Right-stick aim response', value: current.aimResponse, choices: [{ value: 'steady', label: 'Steady' }, { value: 'direct', label: 'Direct' }] },
    { id: 'aim-release', label: 'Released right stick', value: current.aimRelease, choices: [{ value: 'hold', label: 'Hold aim' }, { value: 'follow', label: 'Follow movement' }] },
    { id: 'stick-deadzone', label: 'Stick drift protection', value: current.stickDeadzone, choices: [{ value: 0.12, label: 'Low' }, { value: 0.2, label: 'Standard' }, { value: 0.3, label: 'High' }] },
    { id: 'hp', label: 'Fighter health', value: draft.maxHp, choices: customChoices([25, 50, 100, 200].map((value) => ({ value, label: `${value} HP` })), draft.maxHp, `${draft.maxHp} HP`) },
    { id: 'ft', label: 'Match length', value: draft.firstTo, choices: customChoices(MATCH_LENGTHS, draft.firstTo, `First to ${draft.firstTo}`) },
    { id: 'bots', label: 'Local play bots', value: draft.bots, choices: botChoices(0, MAX_PLAYERS - 1) },
    { id: 'difficulty', label: 'Bot difficulty', value: current.botDifficulty, choices: DIFFICULTIES },
    { id: 'round-limit', label: 'Sudden death after', value: current.roundTimeLimit, choices: customChoices([0, 45, 60, 90].map((value) => ({ value, label: value ? `${value} seconds` : 'Off' })), current.roundTimeLimit, `${current.roundTimeLimit} seconds`) },
    { id: 'rot', label: 'Arena order', value: current.rotation, choices: [{ value: 'random', label: 'Shuffle' }, { value: 'ordered', label: 'In order' }] },
    { id: 'ren', label: 'Graphics', value: current.renderer, choices: [{ value: 'auto', label: 'Auto' }, { value: 'gpu', label: 'Enhanced' }, { value: 'canvas', label: 'Classic' }] },
    { id: 'controller-glyphs', label: 'Controller button labels', value: current.controllerGlyphs, choices: [{ value: 'auto', label: 'Auto' }, { value: 'playstation', label: 'PlayStation' }, { value: 'xbox', label: 'Xbox' }, { value: 'nintendo', label: 'Nintendo' }, { value: 'generic', label: 'Generic' }] },
  ];
  for (const choice of choices) card.querySelector(`[data-control="${choice.id}"]`)!.append(choiceControl(choice));
  card.querySelector('[data-party-settings]')!.append(partyControls(partyOptions(current), 'settings', () => {}, { advanced: true }));

  const favoriteInput = card.querySelector<HTMLInputElement>('#arena-favorites')!;
  const favorites = new Set(current.favoriteArenas);
  favoriteInput.value = JSON.stringify([...favorites]);
  const arenaGrid = card.querySelector<HTMLElement>('[data-collection=arenas]')!;
  for (const level of builtInMatchLevels()) arenaGrid.append(arenaCard(level, {
    checked: current.enabledLevels === 'all' || current.enabledLevels.includes(level.id), favorite: favorites.has(level.id),
    onFavorite: (on) => {
      if (on) favorites.add(level.id); else favorites.delete(level.id);
      favoriteInput.value = JSON.stringify([...favorites]);
      favoriteInput.dispatchEvent(new Event('change', { bubbles: true }));
    },
  }));
  const filters = [false, true].map((only) => {
    const button = btn(only ? 'Favorites' : 'All arenas', () => {
      arenaGrid.dataset.favoritesOnly = String(only);
      filters.forEach((other, i) => other.setAttribute('aria-pressed', String(Boolean(i) === only)));
    });
    button.setAttribute('aria-pressed', String(!only));
    card.querySelector('[data-collection-actions=arenas]')!.append(button);
    return button;
  });

  const updateMatchControls = () => {
    const race = draft.settings.mode === 'arms-race';
    for (const id of ['ft', 'round-limit']) card.querySelector<HTMLElement>(`[data-control="${id}"]`)!.hidden = race;
    card.querySelector<HTMLElement>('[data-race-rules]')!.hidden = !race;
  };
  updateMatchControls();

  const rememberDraft = () => {
    const menuDraft = { ...state, maxHp: draft.maxHp, firstTo: draft.firstTo, bots: draft.bots };
    draft.settings = collectSettings(card, menuDraft, draft.settings);
    draft.maxHp = menuDraft.maxHp;
    draft.firstTo = menuDraft.firstTo;
    draft.bots = menuDraft.bots;
    const pad = collectPadMap(card);
    draft.padId = (card.querySelector('#padid') as HTMLInputElement).value;
    draft.map = pad.map;
    updateMatchControls();
  };
  card.addEventListener('input', rememberDraft);
  card.addEventListener('change', rememberDraft);
  for (const id of ['sfx', 'mus']) {
    const input = card.querySelector<HTMLInputElement>(`#${id}`)!;
    input.addEventListener('input', () => { card.querySelector<HTMLOutputElement>(`output[for="${id}"]`)!.value = `${Math.round(Number(input.value) * 100)}%`; });
  }
  const tabs: { id: SettingsTab; label: string }[] = [
    { id: 'feel', label: 'Sound & feel' }, { id: 'controls', label: 'Controls' }, { id: 'match', label: 'Match' }, { id: 'weapons', label: 'Weapons' }, { id: 'arenas', label: 'Arenas' }, { id: 'advanced', label: 'Advanced' },
  ];
  const setTab = (id: SettingsTab) => {
    state.settingsTab = id;
    for (const panel of card.querySelectorAll<HTMLElement>('[data-panel]')) panel.hidden = panel.dataset.panel !== id;
    for (const tab of card.querySelectorAll<HTMLButtonElement>('[data-settings-tab]')) {
      const selected = tab.dataset.settingsTab === id;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    }
    card.querySelector('.settings-panels')!.scrollTop = 0;
  };
  for (const tab of tabs) {
    const button = document.createElement('button');
    button.type = 'button';
    button.id = `settings-tab-${tab.id}`;
    button.className = 'settings-tab';
    button.dataset.settingsTab = tab.id;
    button.setAttribute('role', 'tab');
    button.setAttribute('aria-controls', `settings-${tab.id}`);
    button.textContent = tab.label;
    button.addEventListener('click', () => setTab(tab.id));
    card.querySelector('.settings-tabs')!.append(button);
  }
  setTab(state.settingsTab);
  for (const collection of ['weapons', 'arenas']) {
    const actionsRow = card.querySelector(`[data-collection-actions="${collection}"]`)!;
    for (const enabled of [true, false]) {
      actionsRow.append(btn(enabled ? 'Select all' : 'Clear all', () => {
        for (const input of card.querySelectorAll<HTMLInputElement>(`[data-collection="${collection}"] input`)) input.checked = enabled;
        rememberDraft();
      }));
    }
  }
  card.querySelector('[data-remap-actions]')!.append(btn('Save remap', () => { rememberDraft(); actions.saveRemap?.(); }));
  const row = document.createElement('div');
  row.className = 'menu-actions settings-actions';
  row.append(
    btn('Save', () => {
      const next = collectSettings(card, state, settings);
      Object.assign(settings, next);
      actions.save?.();
    }, 'primary'),
    btn('Back', () => actions.back?.()),
    navigationHint(),
  );
  card.append(row);
}

/** Pull the lobby's editable fields back into state before an action reads them. */
export function syncLobbyFields(card: HTMLElement, state: MenuState): void {
  const room = card.querySelector('#room') as HTMLInputElement | null;
  if (room) state.roomCode = normalizeRoomCode(room.value);
  const name = card.querySelector('#name') as HTMLInputElement | null;
  if (name) state.lobby.name = cleanPeerName(name.value, '');
  const hp = card.querySelector('#hp') as HTMLInputElement | null;
  if (hp) state.lobby.maxHp = Math.max(1, Number(hp.value) || state.lobby.maxHp);
  const ft = card.querySelector('#ft') as HTMLInputElement | null;
  if (ft) state.lobby.firstTo = Math.max(0, Number(ft.value) || 0);
}

function esc(text: string): string {
  return text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

function renderLobby(card: HTMLElement, state: MenuState, settings: UserSettings, actions: MenuActions): void {
  const lobby = state.lobby;
  if (lobby.phase === 'idle') {
    const linked = isRoomCode(state.roomCode);
    card.innerHTML = `<h2>Online</h2>
      <p>Make a party, share the invite, and keep playing together. Change modes and looks between matches.</p>
      <div class="form-grid">
        <label class="row"><span>Your name</span><input id="name" value="${esc(lobby.name)}" placeholder="Player" maxlength="20" autocomplete="nickname"></label>
        <label class="row"><span>Room code</span><input id="room" value="${esc(state.roomCode)}" placeholder="leave blank to make one" maxlength="8" autocapitalize="characters" spellcheck="false"></label>
      </div>
      <p class="dim">Play with keyboard and mouse or a controller. The party stays open while its host is connected.</p>`;
    const room = card.querySelector('#room') as HTMLInputElement;
    room.addEventListener('input', () => {
      const at = room.selectionStart;
      room.value = normalizeRoomCode(room.value);
      room.setSelectionRange(at, at);
    });
    card.querySelector('#name')?.addEventListener('change', () => {
      syncLobbyFields(card, state);
      actions.lobbyChanged?.();
    });
    const row = document.createElement('div');
    row.className = 'menu-actions';
    const host = btn(
      'Host',
      () => {
        syncLobbyFields(card, state);
        actions.host?.();
      },
      linked ? '' : 'primary',
    );
    const join = btn(
      'Join',
      () => {
        syncLobbyFields(card, state);
        actions.joinRoom?.();
      },
      linked ? 'primary' : '',
    );
    join.id = 'join-room';
    row.append(host, join, btn('Back', () => actions.back?.()));
    card.append(row);
    return;
  }

  if (lobby.phase === 'connecting') {
    card.classList.add('compact');
    card.innerHTML = `<h2>Connecting…</h2><p>Reaching room <b>${esc(state.roomCode)}</b>.</p>`;
    const row = document.createElement('div');
    row.className = 'menu-actions';
    row.append(btn('Cancel', () => actions.leave?.()));
    card.append(row);
    return;
  }

  renderConnectedParty(card, state, settings, actions, () => syncLobbyFields(card, state));
}

function btn(label: string, onClick: () => void, variant = ''): HTMLButtonElement {
  const b = document.createElement('button');
  b.textContent = label;
  b.className = `menu-btn${variant ? ` ${variant}` : ''}`;
  b.addEventListener('click', onClick);
  return b;
}
